import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  Divider,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemText,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Close as CloseIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { StaffPayrollDto, PayrollStatus, PayrollItemDto } from '../../services/payrollService';
import { payrollService } from '../../services/payrollService';

interface PayrollDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  payroll: StaffPayrollDto | null;
  onPrint?: () => void;
  onDownload?: () => void;
}

const getStatusColor = (status: PayrollStatus): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (status) {
    case PayrollStatus.Draft:
      return 'default';
    case PayrollStatus.PendingStaffApproval:
      return 'warning';
    case PayrollStatus.StaffApproved:
      return 'info';
    case PayrollStatus.PendingAdminApproval:
      return 'warning';
    case PayrollStatus.AdminApproved:
      return 'success';
    case PayrollStatus.PendingPayment:
      return 'primary';
    case PayrollStatus.Paid:
      return 'success';
    case PayrollStatus.Rejected:
      return 'error';
    default:
      return 'default';
  }
};

export const PayrollDetailsDialog: React.FC<PayrollDetailsDialogProps> = ({
  open,
  onClose,
  payroll,
  onPrint,
  onDownload
}) => {
  if (!payroll) return null;

  // ATO PAYG (NAT 1004 / NAT 3539, FY 2025-26). The "Other Deductions"
  // legacy slot is intentionally hidden when it's $0 — the new payroll
  // calculator never populates it (super is paid on top, not deducted).
  const taxScaleLabel = payroll.taxScale
    ? payroll.taxScale.replace(/([A-Z])/g, ' $1').trim()
    : null;
  const deductionItems = [
    {
      label: taxScaleLabel
        ? `PAYG Withholding (${taxScaleLabel})`
        : 'PAYG Withholding',
      amount: payroll.taxDeductions,
    },
    payroll.otherDeductions > 0
      ? { label: 'Other Deductions', amount: payroll.otherDeductions }
      : null,
  ].filter((x): x is { label: string; amount: number } => x !== null && x.amount > 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <ViewIcon />
          <Typography variant="h6">Payroll Details</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          {/* Header Info */}
          <Paper sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="h6">{payroll.staffName}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Employee ID: {payroll.employeeId}
                </Typography>
              </Box>
              <Chip
                label={payroll.statusText}
                color={getStatusColor(payroll.status)}
                size="medium"
              />
            </Stack>
          </Paper>

          {/* Period Info */}
          <Box>
            <Typography variant="h6" gutterBottom>Payroll Period</Typography>
            <Stack direction="row" spacing={4}>
              <Box>
                <Typography variant="body2" color="text.secondary">Start Date</Typography>
                <Typography variant="body1">{payrollService.formatDate(payroll.payrollStartDate)}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">End Date</Typography>
                <Typography variant="body1">{payrollService.formatDate(payroll.payrollEndDate)}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Frequency</Typography>
                <Typography variant="body1">{payroll.payrollFrequencyText}</Typography>
              </Box>
            </Stack>
          </Box>

          <Divider />

          {/* Hours Summary */}
          <Box>
            <Typography variant="h6" gutterBottom>Hours Summary</Typography>
            <Stack direction="row" spacing={4}>
              <Box>
                <Typography variant="h4" color="primary">{payroll.totalHours}</Typography>
                <Typography variant="body2" color="text.secondary">Total Hours</Typography>
              </Box>
              <Box>
                <Typography variant="h4" color="success.main">{payroll.regularHours}</Typography>
                <Typography variant="body2" color="text.secondary">Regular Hours</Typography>
              </Box>
              <Box>
                <Typography variant="h4" color="warning.main">{payroll.overtimeHours}</Typography>
                <Typography variant="body2" color="text.secondary">Overtime Hours</Typography>
              </Box>
            </Stack>
          </Box>

          <Divider />

          {/* Pay Summary */}
          <Box>
            <Typography variant="h6" gutterBottom>Pay Summary</Typography>
            <Alert severity="info" icon={<InfoIcon fontSize="small" />} sx={{ mb: 2 }}>
              Pay is calculated from <strong>Billing Rates → Staff Rates</strong>: each shift is sliced by
              day of week and time of day, then priced as
              <strong> base hourly rate × the matching StaffPay segment %</strong>. The hourly rate stored
              on the staff profile is intentionally not used.
            </Alert>
            <Stack spacing={1.5}>
              <Stack direction="row" justifyContent="space-between">
                <Typography>Base hourly rate (from Billing Rates)</Typography>
                <Typography fontWeight="bold">
                  {payroll.hourlyRate > 0
                    ? `${payrollService.formatCurrency(payroll.hourlyRate)}/hr`
                    : 'Not configured'}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography>Hours worked</Typography>
                <Typography fontWeight="bold">{payroll.totalHours.toFixed(2)} h</Typography>
              </Stack>
              <Divider />
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="h6">Gross Pay</Typography>
                <Typography variant="h6" color="primary">
                  {payrollService.formatCurrency(payroll.grossPay)}
                </Typography>
              </Stack>
              {payroll.hourlyRate <= 0 && (
                <Alert severity="warning">
                  No active <strong>StaffBaseRate</strong> is configured for this staff member, so pay is
                  $0. Set one up under <em>Billing Rates → Staff Rates</em> and regenerate this payroll.
                </Alert>
              )}
            </Stack>
          </Box>

          {/* Per-segment breakdown driven by Billing Rates */}
          {payroll.payrollItems && payroll.payrollItems.length > 0 && (
            <>
              <Divider />
              <Box>
                <Typography variant="h6" gutterBottom>Rate Segment Breakdown</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  One row per Billing-Rate StaffPay segment that applied to a shift.
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Segment</TableCell>
                        <TableCell align="right">Hours</TableCell>
                        <TableCell align="right">Effective Rate</TableCell>
                        <TableCell align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payroll.payrollItems
                        .filter((item: PayrollItemDto) => !item.isDeduction)
                        .map((item: PayrollItemDto) => (
                          <TableRow key={item.id}>
                            <TableCell>
                              {item.shiftDate ? payrollService.formatDate(item.shiftDate) : '—'}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{item.description}</Typography>
                            </TableCell>
                            <TableCell align="right">{Number(item.quantity).toFixed(2)}</TableCell>
                            <TableCell align="right">
                              {payrollService.formatCurrency(item.rate)}/hr
                            </TableCell>
                            <TableCell align="right">
                              <Typography fontWeight="bold">
                                {payrollService.formatCurrency(item.amount)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </>
          )}

          {/* Deductions */}
          {deductionItems.length > 0 && (
            <>
              <Divider />
              <Box>
                <Typography variant="h6" gutterBottom>Deductions</Typography>
                <List dense>
                  {deductionItems.map((item, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={item.label} />
                      <Typography color="error">
                        -{payrollService.formatCurrency(item.amount)}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
              </Box>
            </>
          )}

          {/* Employer Superannuation (paid on top, NOT deducted from gross) */}
          {payroll.superContribution !== undefined && payroll.superContribution > 0 && (
            <>
              <Divider />
              <Box>
                <Typography variant="h6" gutterBottom>Superannuation Guarantee (Employer-Paid)</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  12% of OTE — paid directly by the employer to the staff member's super fund.
                  Not deducted from gross.
                </Typography>
                <Stack direction="row" justifyContent="space-between">
                  <Typography>SG Contribution</Typography>
                  <Typography color="success.main" fontWeight="bold">
                    +{payrollService.formatCurrency(payroll.superContribution)}
                  </Typography>
                </Stack>
              </Box>
            </>
          )}

          {/* Tax Declaration & Banking & Super (admin reconciliation block) */}
          {(payroll.maskedTaxFileNumber || payroll.abn || payroll.bankBsb || payroll.superFundName) && (
            <>
              <Divider />
              <Box>
                <Typography variant="h6" gutterBottom>Tax / Bank / Super</Typography>
                <Stack direction="row" spacing={4} flexWrap="wrap" sx={{ rowGap: 2 }}>
                  <Box minWidth={180}>
                    <Typography variant="caption" color="text.secondary">TFN</Typography>
                    <Typography>{payroll.maskedTaxFileNumber || '—'}</Typography>
                  </Box>
                  <Box minWidth={180}>
                    <Typography variant="caption" color="text.secondary">ABN</Typography>
                    <Typography>{payroll.abn || '—'}</Typography>
                  </Box>
                  <Box minWidth={180}>
                    <Typography variant="caption" color="text.secondary">Tax Scale</Typography>
                    <Typography>{taxScaleLabel || '—'}</Typography>
                  </Box>
                  <Box minWidth={180}>
                    <Typography variant="caption" color="text.secondary">STP Income Type</Typography>
                    <Typography>{payroll.stpIncomeType || 'SAW'}</Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={4} flexWrap="wrap" sx={{ mt: 2, rowGap: 2 }}>
                  <Box minWidth={180}>
                    <Typography variant="caption" color="text.secondary">Bank Account</Typography>
                    <Typography>{payroll.bankAccountName || '—'}</Typography>
                  </Box>
                  <Box minWidth={180}>
                    <Typography variant="caption" color="text.secondary">BSB</Typography>
                    <Typography>{payroll.bankBsb || '—'}</Typography>
                  </Box>
                  <Box minWidth={180}>
                    <Typography variant="caption" color="text.secondary">Account #</Typography>
                    <Typography>{payroll.maskedBankAccountNumber || '—'}</Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={4} flexWrap="wrap" sx={{ mt: 2, rowGap: 2 }}>
                  <Box minWidth={180}>
                    <Typography variant="caption" color="text.secondary">Super Fund</Typography>
                    <Typography>{payroll.superFundName || '—'}</Typography>
                  </Box>
                  <Box minWidth={180}>
                    <Typography variant="caption" color="text.secondary">USI</Typography>
                    <Typography>{payroll.superFundUsi || '—'}</Typography>
                  </Box>
                  <Box minWidth={180}>
                    <Typography variant="caption" color="text.secondary">Fund ABN</Typography>
                    <Typography>{payroll.superFundAbn || '—'}</Typography>
                  </Box>
                  <Box minWidth={180}>
                    <Typography variant="caption" color="text.secondary">Member #</Typography>
                    <Typography>{payroll.superMemberNumber || '—'}</Typography>
                  </Box>
                </Stack>
              </Box>
            </>
          )}

          <Divider />

          {/* Net Pay */}
          <Stack direction="row" justifyContent="space-between" sx={{ p: 2, bgcolor: 'primary.light' }}>
            <Typography variant="h5">Net Pay</Typography>
            <Typography variant="h5" color="primary" fontWeight="bold">
              {payrollService.formatCurrency(payroll.netPay)}
            </Typography>
          </Stack>

          {/* Additional Info */}
          {(payroll.adminApprovalNotes || payroll.paymentNotes || payroll.generatedDate) && (
            <>
              <Divider />
              <Box>
                <Typography variant="h6" gutterBottom>Additional Information</Typography>
                {payroll.generatedDate && (
                  <Typography variant="body2" color="text.secondary">
                    Generated: {payrollService.formatDate(payroll.generatedDate)}
                  </Typography>
                )}
                {payroll.adminApprovalNotes && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Admin Notes: {payroll.adminApprovalNotes}
                  </Typography>
                )}
                {payroll.paymentNotes && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Payment Notes: {payroll.paymentNotes}
                  </Typography>
                )}
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {onPrint && (
          <Button onClick={onPrint} startIcon={<PrintIcon />}>
            Print
          </Button>
        )}
        {onDownload && (
          <Button onClick={onDownload} startIcon={<DownloadIcon />}>
            Download PDF
          </Button>
        )}
        <Button onClick={onClose} startIcon={<CloseIcon />}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
